import React, { useMemo, useState } from "react";
import { Plus, MapPin, Trash2, Utensils, ShoppingBag, Mountain, Pin as PinIcon, LayoutGrid } from "lucide-react";
import UploadPhotoButton from "@/components/UploadPhotoButton";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "restaurant", label: "Restaurants", Icon: Utensils, color: "tb-coral" },
  { id: "shop",       label: "Shops",       Icon: ShoppingBag, color: "tb-blue" },
  { id: "activity",   label: "Activities",  Icon: Mountain,   color: "tb-orange" },
  { id: "place",      label: "Other",       Icon: PinIcon,    color: "tb-purple" },
];
// 2026-07 UX: prepend an "All" tab so users can browse pins across
// every category at once. Default landing tab. AddPin dialog still
// requires a concrete category — "all" is view-only.
const ALL_TAB = { id: "all", label: "All", Icon: LayoutGrid, color: "tb-navy" };
const TABS = [ALL_TAB, ...CATEGORIES];

function AddPinDialog({ defaultCategory, onClose, onSaved, createPath }) {
  const [form, setForm] = useState({
    category: defaultCategory || "restaurant",
    name: "",
    location: "",
    note: "",
    image: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() && !form.location.trim()) {
      toast.error("Add a name or a place");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(createPath || "/dream/pins", form);
      toast.success("Pinned ✦");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't pin");
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="pinboard-add-dialog"
      className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3"
      role="dialog"
      aria-modal="true"
    >
      <form onSubmit={submit} className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-md w-full max-h-[92vh] overflow-y-auto">
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span className="font-hand text-xl">add a pin</span>
          <button type="button" onClick={onClose} className="font-hand text-tb-gold tb-tap">close</button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <span className="font-hand text-tb-navy/65 text-sm">Category</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`pinboard-cat-${c.id}`}
                  onClick={() => setForm({ ...form, category: c.id })}
                  className={
                    "px-3 py-1.5 rounded-full font-hand text-sm border-2 tb-tap " +
                    (form.category === c.id
                      ? "bg-tb-navy text-tb-gold-bright border-tb-gold"
                      : "bg-tb-cream-2 text-tb-navy/80 border-tb-cream-3 hover:border-tb-gold/60")
                  }
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="font-hand text-tb-navy/65 text-sm">Name</span>
            <input
              data-testid="pinboard-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Quimet & Quimet"
              className="w-full px-3 py-2 rounded-lg bg-tb-cream border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
            />
          </label>
          <label className="block">
            <span className="font-hand text-tb-navy/65 text-sm">Location</span>
            <input
              data-testid="pinboard-location-input"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Barcelona, Spain"
              className="w-full px-3 py-2 rounded-lg bg-tb-cream border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
            />
          </label>
          <label className="block">
            <span className="font-hand text-tb-navy/65 text-sm">Note (optional)</span>
            <textarea
              data-testid="pinboard-note-input"
              rows={2} value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="why you want to visit…"
              className="w-full px-3 py-2 rounded-lg bg-tb-cream border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
            />
          </label>
          <div>
            <span className="font-hand text-tb-navy/65 text-sm">Photo (optional)</span>
            <div className="mt-1">
              <UploadPhotoButton
                testId="pinboard-image-input"
                value={form.image}
                onChange={(v) => setForm({ ...form, image: v })}
                max={1}
                label={form.image ? "Replace photo" : "Add photo"}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="pinboard-submit-btn"
            className="w-full py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap disabled:opacity-60"
          >
            {busy ? "pinning…" : "pin it"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PinCard({ pin, onRemove }) {
  return (
    <div
      data-testid={`pinboard-pin-${pin.id}`}
      className="rounded-2xl bg-tb-cream border-2 border-tb-cream-3 overflow-hidden flex flex-col"
    >
      {pin.image ? (
        <img src={pin.image} alt="" className="w-full h-24 object-cover" />
      ) : (
        <div className="w-full h-24 bg-tb-cream-2 flex items-center justify-center">
          <PinIcon className="w-6 h-6 text-tb-navy/30" />
        </div>
      )}
      <div className="p-2 flex-1 flex flex-col">
        <div className="font-serif-it text-tb-navy text-base leading-tight truncate">
          {pin.name || pin.note || "Untitled pin"}
        </div>
        {pin.location && (
          <p className="font-hand text-tb-navy/65 text-xs flex items-center gap-1 mt-0.5 truncate">
            <MapPin className="w-3 h-3" /> {pin.location}
          </p>
        )}
        {pin.note && pin.name && (
          <p className="font-ui text-xs text-tb-navy/65 mt-1 line-clamp-2">{pin.note}</p>
        )}
        <button
          onClick={() => onRemove(pin)}
          data-testid={`pinboard-remove-${pin.id}`}
          className="mt-auto self-end flex items-center gap-1 font-hand text-xs text-tb-coral hover:text-red-700 tb-tap pt-1"
          aria-label="Remove pin"
        >
          <Trash2 className="w-3 h-3" /> remove
        </button>
      </div>
    </div>
  );
}

export default function PinBoard({ pins = [], onChange, endpoints }) {
  const [tab, setTab] = useState("all");
  const [adding, setAdding] = useState(false);
  const grouped = useMemo(() => {
    const out = { all: [], restaurant: [], shop: [], activity: [], place: [] };
    for (const p of pins) {
      const cat = (p.category || "place").toLowerCase();
      const bucket = out[cat] || out.place;
      bucket.push(p);
      out.all.push(p);
    }
    return out;
  }, [pins]);

  // Endpoints default to the legacy singleton dream so pre-existing callers
  // (currently: nothing after the multi-vacation cutover, retained for
  // backwards compat) keep working. Per-vacation pages pass scoped paths.
  const createPath = endpoints?.create || "/dream/pins";
  const removePathFor = endpoints?.remove || ((pin) => `/dream/pins/${pin.id}`);

  const removePin = async (pin) => {
    try {
      const { data } = await api.delete(removePathFor(pin));
      onChange?.(data);
      toast.success("Pin removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't remove");
    }
  };

  const list = grouped[tab] || [];
  return (
    <section data-testid="pinboard-section" className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-it text-tb-navy text-xl flex items-center gap-2">
          <PinIcon className="w-5 h-5 text-tb-coral" /> Pin Board
        </h3>
        <button
          data-testid="pinboard-add-btn"
          onClick={() => setAdding(true)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-sm tb-tap"
        >
          <Plus className="w-3.5 h-3.5" /> add pin
        </button>
      </div>
      <p className="font-hand text-tb-navy/60 italic text-sm">
        pin restaurants, shops, activities & anywhere you want to remember
      </p>

      <div className="flex flex-wrap gap-2">
        {TABS.map((c) => (
          <button
            key={c.id}
            data-testid={`pinboard-tab-${c.id}`}
            onClick={() => setTab(c.id)}
            className={
              "px-3 py-1 rounded-full font-hand text-sm border-2 tb-tap flex items-center gap-1.5 " +
              (tab === c.id
                ? "bg-tb-navy text-tb-gold-bright border-tb-gold"
                : "bg-tb-cream-2 text-tb-navy/80 border-tb-cream-3 hover:border-tb-gold/60")
            }
          >
            <c.Icon className="w-3.5 h-3.5" /> {c.label}
            <span className="text-xs text-tb-navy/50">({(grouped[c.id] || []).length})</span>
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="text-center py-6 border-2 border-dashed border-tb-cream-3 rounded-2xl bg-tb-cream-2/40">
          <p className="font-hand text-tb-navy/55 italic text-sm">
            no pins here yet — tap <b>add pin</b> to start
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {list.map((p) => (
            <PinCard key={p.id} pin={p} onRemove={removePin} />
          ))}
        </div>
      )}

      {adding && (
        <AddPinDialog
          defaultCategory={tab === "all" ? "restaurant" : tab}
          createPath={createPath}
          onClose={() => setAdding(false)}
          onSaved={(d) => { onChange?.(d); setAdding(false); }}
        />
      )}
    </section>
  );
}
